import React, { useState, useEffect } from 'react';
import { Search } from 'lucide-react';
import Select from 'react-select';
import { API_ENDPOINTS } from '../../config/properties';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import httpClient from '../../services/httpClient';
import './TeamStatsTable.css';

const TeamStatsTable = () => {
  const { t } = useTranslation();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [teamCode, setTeamCode] = useState('');
  const [lagueCode, setLagueCode] = useState('');
  const [year, setYear] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'delayDraw', direction: 'desc' });
  const [data, setData] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [teams, setTeams] = useState([]);
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getCircleColorClass = (value) => {
    if (value > 15) {
      return 'critical';
    } else if (value >= 11) {
      return 'warning-high';
    } else if (value >= 5) {
      return 'warning';
    } else if (value >= 3) {
      return 'info';
    } else {
      return 'light';
    }
  };

  const renderCircleValue = (value) => (
    <span className={`delay-circle ${getCircleColorClass(value)}`}>
      {value}
    </span>
  );

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!isAuthenticated) {
      setError('Authentication required');
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      const params = new URLSearchParams({
        year,
        lagueCode,
        teamCode,
        orderColumn: sortConfig.key,
        orderDir: sortConfig.direction,
        page: 0,
        size: 100,
      });

      try {
        setError(null);
        const response = await httpClient.get(`${API_ENDPOINTS.statsTeams}?${params.toString()}`);
        const result = await response.json();
        setData(result.data || []);
      } catch (error) {
        console.error('Errore nel caricamento dei dati:', error);
        setError(t('errorLoadingData') || 'Error loading data');
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [year, lagueCode, teamCode, sortConfig, authLoading, isAuthenticated, t]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!isAuthenticated) {
      setError('Authentication required');
      return;
    }

    const fetchLeagues = async () => {
      try {
        setError(null);
        const response = await httpClient.get(API_ENDPOINTS.leagues);
        const result = await response.json();
        setLeagues(result || []);
      } catch (error) {
        console.error('Errore nel caricamento delle leghe:', error);
        setError(t('errorLoadingLeagues') || 'Error loading leagues');
      }
    };

    const fetchYears = async () => {
      try {
        setError(null);
        const response = await httpClient.get(API_ENDPOINTS.years);
        const result = await response.json();
        setYears(result || []);
      } catch (error) {
        console.error('Errore nel caricamento degli anni:', error);
        setError(t('errorLoadingYears') || 'Error loading years');
      }
    };

    fetchLeagues();
    fetchYears();
  }, [authLoading, isAuthenticated, t]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!isAuthenticated) {
      setError('Authentication required');
      return;
    }

    const fetchTeams = async () => {
      if (!lagueCode || !year) {
        setTeams([]);
        return;
      }

      setLoading(true);
      try {
        setError(null);
        const response = await httpClient.get(`${API_ENDPOINTS.teams}?year=${year}&lagueCode=${lagueCode}`);
        const result = await response.json();
        const formattedTeams = result.map((team) => ({
          value: team.code,
          label: `${team.name} (${team.lague.name} - ${team.lague.country})`,
        }));
        setTeams(formattedTeams || []);
      } catch (error) {
        console.error('Errore nel caricamento dei team:', error);
        setError(t('errorLoadingTeams') || 'Error loading teams');
        setTeams([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTeams();
  }, [lagueCode, year, authLoading, isAuthenticated, t]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Show loading state while auth is initializing
  if (authLoading) {
    return (
      <div className="container mt-5">
        <div className="alert alert-info">
          {t('loading') || 'Loading...'}
        </div>
      </div>
    );
  }

  // Show error if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="container mt-5">
        <div className="alert alert-warning">
          {t('authenticationRequired') || 'Authentication required. Please log in.'}
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      <div className="team-stats-header">
        <h1>{t('titlePageTabTeamStats')}</h1>
        <p className="mb-0 mt-2">
          {t('viewTeamStatistics') || 'Analyze team performance metrics and delays'}
        </p>
      </div>

      {error && (
        <div className="alert alert-danger mb-3">
          {error}
        </div>
      )}

      <div className="card shadow-lg">
        <div className="card-body p-4">
          <div className="championship-filters mb-4">
            <div className="row g-3">
              <div className="col-md-3">
                <label htmlFor="year" className="form-label fw-600">{t('year')}</label>
                <Select
                  id="year"
                  options={[{ value: '', label: t('anyYear') }, ...years.map((yearOption) => ({
                    value: yearOption,
                    label: yearOption,
                  }))]}
                  value={year ? { value: year, label: year } : { value: '', label: t('anyYear') }}
                  onChange={(selectedOption) => setYear(selectedOption?.value || '')}
                  isLoading={loading}
                  placeholder={t('selectYear')}
                />
              </div>
              <div className="col-md-3">
                <label htmlFor="lagueCode" className="form-label fw-600">{t('league')}</label>
                <Select
                  id="lagueCode"
                  options={[{ value: '', label: t('anyLeague') }, ...leagues.map((league) => ({
                    value: league.code,
                    label: league.name,
                  }))]}
                  value={lagueCode ? { value: lagueCode, label: leagues.find((league) => league.code === lagueCode)?.name } : { value: '', label: t('anyLeague') }}
                  onChange={(selectedOption) => setLagueCode(selectedOption?.value || '')}
                  isLoading={loading}
                  placeholder={t('selectLeague')}
                />
              </div>
              <div className="col-md-3">
                <label htmlFor="teamCode" className="form-label fw-600">{t('teamCode')}</label>
                <Select
                  id="teamCode"
                  options={[{ value: '', label: t('anyTeam') }, ...teams]}
                  value={teamCode ? { value: teamCode, label: teams.find((team) => team.value === teamCode)?.label } : { value: '', label: t('anyTeam') }}
                  onChange={(selectedOption) => setTeamCode(selectedOption?.value || '')}
                  isLoading={loading}
                  placeholder={t('selectTeam')}
                />
              </div>
              <div className="col-md-3">
                <label htmlFor="search" className="form-label fw-600">{t('search')}</label>
                <div className="position-relative">
                  <input
                    id="search"
                    type="text"
                    placeholder={t('teamSearchPlaceholder')}
                    className="form-control"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Search className="position-absolute top-50 start-0 translate-middle-y ms-2 search-icon" size={20} />
                </div>
              </div>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th style={{ minWidth: '200px' }}>{t('team')}</th>
                  <th onClick={() => handleSort('delayDraw')} className="sortable text-center">
                    {t('gamesWithoutDraw')}
                    <span className={`sort-indicator ${sortConfig.key === 'delayDraw' ? 'active' : ''}`}>
                      {sortConfig.key === 'delayDraw' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
                    </span>
                  </th>
                  <th onClick={() => handleSort('delayVictory')} className="sortable text-center">
                    {t('gamesWithoutVictory')}
                    <span className={`sort-indicator ${sortConfig.key === 'delayVictory' ? 'active' : ''}`}>
                      {sortConfig.key === 'delayVictory' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
                    </span>
                  </th>
                  <th onClick={() => handleSort('delayLost')} className="sortable text-center">
                    {t('gamesWithoutLoss')}
                    <span className={`sort-indicator ${sortConfig.key === 'delayLost' ? 'active' : ''}`}>
                      {sortConfig.key === 'delayLost' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
                    </span>
                  </th>
                  <th className="text-center">{t('year')}</th>
                </tr>
              </thead>
              <tbody>
                {data.filter((team) =>
                  team.teamName.toLowerCase().includes(searchTerm.toLowerCase())
                ).map((team) => (
                  <tr key={team.id}>
                    <td>
                      <strong className="team-name">{team.teamName}</strong>
                      <div className="team-league-info">
                        {(team.lagueTeam && team.lagueTeam.lague) ? `${team.lagueTeam.lague.name} - ${team.lagueTeam.lague.country} (${team.lagueCode})` : team.lagueCode}
                      </div>
                    </td>
                    <td className="text-center">{renderCircleValue(team.delayDraw)}</td>
                    <td className="text-center">{renderCircleValue(team.delayVictory)}</td>
                    <td className="text-center">{renderCircleValue(team.delayLost)}</td>
                    <td className="text-center"><span className="year-badge">{team.year}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamStatsTable;
